type Scalar = string | number | boolean | null
const defaults = {
    decimalsMax: 2,
    decimalsMin: 2,
    locale: 'sv-SE',
}

export type MaybeArray<T> = T | readonly T[]
export const tupleKey = <T extends readonly unknown[]>(...parts: T) => parts

export function IsNullOrUndefined(value: unknown): value is null | undefined {
    return value == null
}

export function IsDefined<T>(value: T | null | undefined): value is T {
    return value != null
}

export function IsNullOrEmpty(value: unknown): boolean {
    return IsNullOrUndefined(value) || value === ''
}

export function IsArrayPopulated(value: unknown): value is readonly unknown[] {
    return Array.isArray(value) && value.length > 0
}

export function IsArrayEmpty(value: unknown): boolean {
    return Array.isArray(value) && value.length === 0
}

export function ArrayDistinct<T>(array: readonly T[]): T[] {
    return Array.from(new Set(array))
}

const IsArrayOf = <T>(value: MaybeArray<T>): value is readonly T[] => Array.isArray(value)

export const AsArray = <T>(value: MaybeArray<T> | undefined): readonly T[] => {
    if (value === undefined) return []
    return IsArrayOf(value) ? value : [value]
}

export function IsPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') return false
    if (Array.isArray(value)) return false

    return Object.getPrototypeOf(value) === Object.prototype
}

export function IsObjectPopulated(value: unknown): value is Record<string, unknown> {
    return IsPlainObject(value) && Object.keys(value).length > 0
}

export function IsObjectEmpty(value: unknown): value is Record<string, unknown> {
    return IsPlainObject(value) && Object.keys(value).length === 0
}

export function IsFunction(value: unknown): value is (...args: unknown[]) => unknown {
    return typeof value === 'function'
}

export function IsUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)
}

export function isScalar(value: unknown): value is Scalar {
    return (
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    )
}

export function ObjectToArray(object: any): any[] {
    const values: any[] = []

    if (IsObjectPopulated(object)) {
        for (const [, value] of Object.entries(object)) {
            values.push(value)
        }
    }

    return values
}

export function ValuesWithKeyFromObject(object: any, key: string): any[] {
    const values: any[] = []

    if (IsObjectPopulated(object)) {
        for (const [, value] of Object.entries(object)) {
            if (IsObjectPopulated(value) && value[key]) {
                values.push(value[key])
            }
        }
    }

    return values
}

export function GetLocale(locale?: string) {
    return locale ?? navigator.language ?? defaults.locale
}

export function FormatCurrency(
    value: number | undefined,
    currency: string,
    locale?: string,
): string {
    locale ??= GetLocale(locale)

    return (
        value?.toLocaleString(locale, {
            minimumFractionDigits: defaults.decimalsMin,
            maximumFractionDigits: defaults.decimalsMax,
            style: 'currency',
            currency,
        }) ?? '-'
    )
}

export function FormatDecimal(value: number | null | undefined, locale?: string): string {
    locale ??= GetLocale(locale)

    return (
        value?.toLocaleString(locale, {
            minimumFractionDigits: defaults.decimalsMin,
            maximumFractionDigits: defaults.decimalsMax,
            style: 'decimal',
        }) ?? '-'
    )
}

export function FormatPercentage(value: number | undefined, locale?: string): string {
    if (IsNullOrUndefined(value)) return '-'

    locale ??= GetLocale(locale)

    return (value / 100).toLocaleString(locale, {
        minimumFractionDigits: defaults.decimalsMin,
        maximumFractionDigits: defaults.decimalsMax,
        style: 'percent',
    })
}

export function FormatFileSize(bytes: number | null | undefined): string {
    return `${Math.round(((bytes ?? 0) / (1024 * 1024)) * 100) / 100} MB`
}

export function IsEqualShallow<T extends Record<string, any>>(a: T, b: T): boolean {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)

    if (aKeys.length !== bKeys.length) return false

    return aKeys.every((key) => {
        const k = key as keyof T
        return Object.is(a[k], b[k])
    })
}

export function IsEqualValues<T extends Record<string, any>>(a: T, b: T): boolean {
    return JSON.stringify(a) === JSON.stringify(b)
}

export function SetAllScalarValues(args: {
    value: unknown
    path: string
    setValue: (path: any, value: any, options?: any) => void
}) {
    const { value, path, setValue } = args

    if (value === undefined) return

    if (isScalar(value)) {
        if (path)
            setValue(path, value, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            })
        return
    }

    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            const nextPath = path ? `${path}.${i}` : `${i}`
            SetAllScalarValues({ value: value[i], path: nextPath, setValue })
        }
        return
    }

    if (IsPlainObject(value)) {
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            const nextPath = path ? `${path}.${k}` : k
            SetAllScalarValues({ value: v, path: nextPath, setValue })
        }
    }
}

export function ReconcileById<T extends object>(
    existing: readonly T[],
    updated: readonly T[],
    getId: (x: T) => string,
): T[] {
    const existingById = new Map<string, T>(existing.map((item) => [getId(item), item]))

    return updated.map((item) => {
        const existingItem = existingById.get(getId(item))
        if (!existingItem) return item

        Object.assign(existingItem, item)
        return existingItem
    })
}
